import { FocusTrap } from '@/utils/focustrap';

const imageOverlayContainerSelector = '.image-overlay-container';
const imageOverlayContentSelector = '.image-overlay-content';

class ImageOverlay {
  static instances = [];

  constructor(container) {
    ImageOverlay.instances.push(this);
    this.closeOverlayButtonSelector = '.close-overlay-btn';
    this.container = container;
    this.openButtons = container.querySelectorAll('.open-overlay-btn');
    this.lastFocusedElement = null;
    this.isOpen = false;
    this.init();
  }

  init() {
    this.container.querySelectorAll(imageOverlayContentSelector).forEach((overlay) => {
      overlay.setAttribute('tabindex', '-1');
      overlay.addEventListener('keydown', this.handleEscapeKey.bind(this));
      overlay.focusTrap = new FocusTrap({
        trapElement: overlay,
        autofocus: true,
        skipForceFocusForNonKeyboardInput: true,
      });
    });

    this.openButtons.forEach((button) => {
      const overlayContentId = button.getAttribute('aria-controls');
      const overlayContent = document.getElementById(overlayContentId);
      const closeButton = overlayContent?.querySelector(this.closeOverlayButtonSelector);

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.openOverlay(button, overlayContent);
      });
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeOverlay(overlayContent));
      }
    });
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      this.closeOverlay(event.currentTarget);
    }
  }

  openOverlay(button, overlayContent) {
    ImageOverlay.instances.forEach((overlayInstance) => {
      if (overlayInstance.isOpen && overlayInstance !== this) {
        overlayInstance.closeOverlay(overlayInstance.container.querySelector(imageOverlayContentSelector));
      }
    });

    this.lastFocusedElement = button;
    this.toggleOverlayVisibility(overlayContent, true);
    this.handleOverlayTransition(overlayContent);
    overlayContent.focusTrap.activate();
    this.isOpen = true;
  }

  closeOverlay(overlayContent) {
    overlayContent.focusTrap.deactivate();
    this.toggleOverlayVisibility(overlayContent, false);
    setTimeout(() => {
      this.lastFocusedElement.focus();
    });
    this.isOpen = false;
  }

  handleOverlayTransition(overlayContent) {
    const closeButton = overlayContent.querySelector(this.closeOverlayButtonSelector);
    const handleTransitionEnd = () => {
      if (closeButton) closeButton.focus();
      overlayContent.removeEventListener('transitionend', handleTransitionEnd);
    };
    overlayContent.addEventListener('transitionend', handleTransitionEnd);
  }

  toggleOverlayVisibility(overlayContent, isVisible) {
    if (isVisible) {
      overlayContent.style.display = 'block'; // Ensure the element is visible before adding the class
      requestAnimationFrame(() => {
        overlayContent.classList.add('show');
      });
    } else {
      overlayContent.classList.remove('show');
      overlayContent.addEventListener(
        'transitionend',
        () => {
          overlayContent.style.display = 'none'; // Hide the element after the transition
        },
        { once: true }
      );
    }
    overlayContent.setAttribute('aria-hidden', String(!isVisible));
    this.updateFocusableElements(overlayContent, isVisible);
    this.lastFocusedElement.setAttribute('aria-expanded', String(isVisible));
    this.lastFocusedElement.setAttribute('tabindex', isVisible ? '-1' : '0');
  }

  updateFocusableElements(overlayContent, isVisible) {
    const container = overlayContent.closest(imageOverlayContainerSelector)?.querySelector('.image-container');
    if (!container) return;

    const focusableElements = container.querySelectorAll('button, [href], input, select, textarea, [tabindex]');
    focusableElements.forEach((el) => {
      if (isVisible) {
        el.setAttribute('data-original-tabindex', el.getAttribute('tabindex') || '0');
        el.setAttribute('tabindex', '-1');
      } else {
        this.restoreOriginalTabindex(el);
      }
    });
  }

  restoreOriginalTabindex(element) {
    const originalTabindex = element.getAttribute('data-original-tabindex');
    if (originalTabindex !== null) {
      element.setAttribute('tabindex', originalTabindex);
      element.removeAttribute('data-original-tabindex');
    } else {
      element.removeAttribute('tabindex');
    }
  }
}

function initImageOverlay() {
  document.querySelectorAll(imageOverlayContainerSelector).forEach((container) => new ImageOverlay(container));

  document.addEventListener('click', (event) => {
    const overlayContent = event.target.closest(imageOverlayContentSelector);

    // this is an edge case where trigger element is to paly the video but removed immediately from dom on click
    // so we need to check if the target is not inside the body
    // to avoid closing the overlay
    if (!event.target.closest('body')) {
      return;
    }

    if (!overlayContent) {
      ImageOverlay.instances.forEach((overlayInstance) => {
        if (overlayInstance.isOpen) {
          overlayInstance.closeOverlay(overlayInstance.container.querySelector(imageOverlayContentSelector));
        }
      });
    }
  });
}

if (document.readyState !== 'loading') {
  initImageOverlay();
} else {
  document.addEventListener('DOMContentLoaded', () => initImageOverlay());
}
